import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Search } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { usePagedAvailableGreenhouseStock } from "@/features/production/hooks/useGreenhouseStock";
import { StockSowingDate } from "@/features/production/components/greenhouseStock/StockSowingDate";
import { useActiveVarietyPricings } from "@/features/production/hooks/useVarietyPricings";
import type { VarietyPricing } from "@/features/production/services/varietyPricingApi";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useExpo } from "../hooks/useExpos";
import { useCreateExpoTransfer, useExpoInventory } from "../hooks/useExpoTransfers";

const PRICING_MODE_PER_TRAY = 1;
const PRICING_MODE_PER_SEEDLING = 2;

const parseTrayType = (value?: string | number | null): number | null => {
  if (value === 210 || value === 442) return value;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("210") || normalized.includes("twoten") || normalized.includes("two ten")) return 210;
  if (normalized.includes("442") || normalized.includes("fourfortytwo") || normalized.includes("four forty two")) return 442;
  return null;
};

const normalize = (value?: string | null) => (value ?? "").trim().toLowerCase();

const trayLabel = (value?: string | number | null) => {
  const trayType = parseTrayType(value);
  if (trayType === 210) return "210-hole tray";
  if (trayType === 442) return "442-hole tray";
  return null;
};

const unitLabel = (row: { trayType?: string | null; stockUnit?: string | null; unit?: string | null }, options?: { short?: boolean }) => {
  const tray = trayLabel(row.trayType);
  if (tray) return options?.short ? "trays" : tray;

  const unit = String(row.stockUnit ?? row.unit ?? "units").trim();
  if (!unit) return "units";
  if (unit.toLowerCase() === "trays") return options?.short ? "trays" : "trays";
  if (unit.toLowerCase() === "pieces") return "pieces";
  return unit.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
};

const displayQuantity = (value: number | null | undefined, unit: string) =>
  `${displayQty(value)} ${unit}`;

type TransferRow = {
  id: string;
  greenhouseStockEntryId: string;
  label: string;
  cropTypeName: string;
  variety?: string | null;
  location: string;
  available: number;
  received?: number;
  sold?: number;
  returned?: number;
  pendingReturn?: number;
  onHand?: number;
  unit?: string | null;
  trayType?: string | null;
  stockUnit?: string | null;
  sowingDate?: string | null;
  source?: string | null;
};

const displayQty = (value?: number | null) => Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 4 });

export function ExpoTransferFormPage() {
  const { id = "" } = useParams();
  const [query] = useSearchParams();
  const type = query.get("type") === "return" ? "ReturnToGreenhouse" : "ToExpo";
  const navigate = useNavigate();
  const { data: expo } = useExpo(id);
  const create = useCreateExpoTransfer();
  const { data: pricings = [] } = useActiveVarietyPricings();

  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const greenhouse = usePagedAvailableGreenhouseStock({
    pageNumber,
    pageSize: 25,
    search: type === "ToExpo" ? debouncedSearch || undefined : undefined,
    readyForWalkIn: true,
    enabled: type === "ToExpo",
  });
  const inventory = useExpoInventory(type === "ReturnToGreenhouse" ? id : undefined);

  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Record<string, TransferRow>>({});
  const [error, setError] = useState("");

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, type]);

  const rows = useMemo<TransferRow[]>(() => {
    if (type === "ToExpo") {
      return (greenhouse.data?.items ?? []).map((stock) => ({
        id: stock.entryId,
        greenhouseStockEntryId: stock.entryId,
        label: `${stock.cropTypeName}${stock.variety ? ` · ${stock.variety}` : ""}`,
        cropTypeName: stock.cropTypeName,
        variety: stock.variety,
        location: [
          stock.source,
          stock.greenhouseName,
          stock.bayName ? `Bay ${stock.bayName}` : null,
          stock.bedNumber ? `Bed ${stock.bedNumber}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || "Main stock",
        available: stock.availableQuantity,
        unit: stock.trayType ?? stock.stockUnit,
        trayType: stock.trayType,
        stockUnit: stock.stockUnit,
        sowingDate: stock.sowingDate,
        source: stock.source,
      }));
    }

    const term = debouncedSearch.toLowerCase();
    return (inventory.data ?? [])
      .filter((stock) => stock.availableQuantity > 0)
      .filter((stock) => {
        if (!term) return true;
        return [
          stock.cropTypeName,
          stock.variety,
          stock.stockUnit,
          stock.trayType,
          stock.sourceStockLocation,
        ].some((value) => value?.toLowerCase().includes(term));
      })
      .map((stock) => ({
        id: stock.id,
        greenhouseStockEntryId: stock.greenhouseStockEntryId,
        label: `${stock.cropTypeName}${stock.variety ? ` · ${stock.variety}` : ""}`,
        cropTypeName: stock.cropTypeName,
        variety: stock.variety,
        location: stock.sourceStockLocation || expo?.name || "Expo stock",
        available: stock.availableQuantity,
        received: stock.totalReceivedQuantity,
        sold: stock.totalSoldQuantity,
        returned: stock.totalReturnedQuantity,
        pendingReturn: stock.reservedQuantity,
        onHand: stock.onHandQuantity,
        unit: stock.trayType ?? stock.stockUnit,
        trayType: stock.trayType,
        stockUnit: stock.stockUnit,
      }));
  }, [debouncedSearch, expo?.name, greenhouse.data?.items, inventory.data, type]);

  const patchQuantity = (row: TransferRow, value: string) => {
    setSelectedRows((current) => ({ ...current, [row.id]: row }));
    setQuantities((current) => ({ ...current, [row.id]: value }));
  };

  const configuredPriceFor = (row: TransferRow) => {
    const trayType = parseTrayType(row.trayType);
    const pricingMode = trayType === null ? PRICING_MODE_PER_SEEDLING : PRICING_MODE_PER_TRAY;
    const pricing = pricings.find(
      (price: VarietyPricing) =>
        normalize(price.cropTypeName) === normalize(row.cropTypeName) &&
        normalize(price.varietyName) === normalize(row.variety) &&
        price.pricingMode === pricingMode &&
        parseTrayType(price.trayType) === trayType &&
        price.companySeedlingPrice != null &&
        price.companySeedlingPrice >= 0,
    );
    return pricing?.companySeedlingPrice ?? 0;
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const selectedLines = Object.values(selectedRows)
      .map((row) => ({ row, quantity: Number(quantities[row.id]) }))
      .filter((line) => line.quantity > 0);
    const lines = selectedLines
      .map((row) => ({
        greenhouseStockEntryId: row.row.greenhouseStockEntryId,
        quantity: row.quantity,
        expectedSellingPrice: type === "ToExpo" ? configuredPriceFor(row.row) : 0,
        remarks: null,
      }));

    if (!reason.trim() || lines.length === 0) {
      setError("Reason and at least one positive quantity are required.");
      return;
    }

    if (type === "ToExpo" && Object.values(selectedRows).some((row) => Number(quantities[row.id]) > 0 && configuredPriceFor(row) <= 0)) {
      setError("Expected price is not configured for one or more selected lines. Set up pricing before moving stock to Expo.");
      return;
    }

    const excessiveLine = selectedLines.find((line) => line.quantity > line.row.available);
    if (excessiveLine) {
      setError("A quantity exceeds available stock.");
      return;
    }

    try {
      const transfer = await create.mutateAsync({
        expoId: id,
        transferType: type,
        transferDate: new Date().toISOString(),
        reason,
        remarks: remarks || null,
        lines,
      });
      navigate(`/expos/${id}/transfers/${transfer.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create transfer.");
    }
  };

  const totalPages = greenhouse.data?.totalPages ?? 1;
  const totalCount = type === "ToExpo" ? greenhouse.data?.totalCount ?? 0 : rows.length;
  const selectedCount = Object.values(quantities).filter((value) => Number(value) > 0).length;
  const selectedQuantity = Object.entries(quantities).reduce((total, [rowId, value]) => {
    const row = selectedRows[rowId];
    const qty = Number(value || 0);
    return row && qty > 0 ? total + Math.min(qty, row.available) : total;
  }, 0);
  const totalAvailable = rows.reduce((total, row) => total + row.available, 0);
  const remainingAfterSelection = Math.max(0, totalAvailable - selectedQuantity);
  const overQuantityRows = Object.values(selectedRows).filter(
    (row) => Number(quantities[row.id]) > row.available,
  );
  const hasOverQuantity = overQuantityRows.length > 0;
  const reasonMissing = !reason.trim();
  const hasPositiveSelection = selectedCount > 0;
  const canSubmit = !create.isPending && !hasOverQuantity && !reasonMissing && hasPositiveSelection;
  const returnableRows = type === "ReturnToGreenhouse" ? rows : [];
  const stockGridClass = type === "ReturnToGreenhouse"
    ? "grid min-w-[760px] grid-cols-[minmax(260px,1fr)_120px_120px_120px_120px_160px_120px] gap-3"
    : "grid min-w-[1040px] grid-cols-[minmax(170px,1fr)_minmax(140px,1fr)_110px_130px_130px_150px_190px] gap-3";
  const setReturnAll = (row: TransferRow) => patchQuantity(row, String(row.available));
  const selectAllAvailable = () => {
    const nextQuantities: Record<string, string> = {};
    const nextRows: Record<string, TransferRow> = {};
    for (const row of rows) {
      if (row.available <= 0) continue;
      nextRows[row.id] = row;
      nextQuantities[row.id] = String(row.available);
    }
    setSelectedRows(nextRows);
    setQuantities(nextQuantities);
    setError("");
  };

  return (
    <form onSubmit={submit} className="space-y-5 pb-24">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">
          {type === "ToExpo" ? "Transfer stock to Expo" : "Return unsold stock"}
        </h1>
        <p className="text-muted-foreground">
          {expo?.name} · {type === "ToExpo" ? "greenhouse stock decreases on dispatch, and Expo stock increases on receipt." : "a request reserves stock after approval; Expo stock decreases on dispatch and greenhouse stock increases on receipt."}
        </p>
      </div>

      {type === "ReturnToGreenhouse" && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-lg border p-4 text-sm"><span className="text-muted-foreground">Returnable lines</span><p className="text-2xl font-semibold">{returnableRows.length}</p></div>
          <div className="rounded-lg border p-4 text-sm"><span className="text-muted-foreground">Available unsold</span><p className="text-2xl font-semibold">{displayQty(totalAvailable)}</p></div>
          <div className="rounded-lg border p-4 text-sm"><span className="text-muted-foreground">Selected return</span><p className="text-2xl font-semibold">{displayQty(selectedQuantity)}</p></div>
          <div className="rounded-lg border p-4 text-sm"><span className="text-muted-foreground">Remaining after return</span><p className="text-2xl font-semibold">{displayQty(remainingAfterSelection)}</p></div>
        </div>
      )}

      <div className="grid gap-4 rounded-xl border bg-card p-4 shadow-sm md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">Reason *</label>
          <Textarea
            className="min-h-20"
            placeholder="Why is this stock being moved?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Notes</label>
          <Textarea
            className="min-h-20"
            placeholder="Vehicle, loader, handling notes, or other remarks"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
          />
        </div>
      </div>

      <div className="rounded-xl border bg-card shadow-sm">
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="font-semibold">Stock batches</h2>
            <p className="text-sm text-muted-foreground">
              {type === "ToExpo"
                ? "Server-side search checks crop, variety, customer, source, and stock location."
                : "Search is limited to unsold stock received by this Expo and not reserved by another return."}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
            {type === "ReturnToGreenhouse" && (
              <Button type="button" variant="outline" onClick={selectAllAvailable} disabled={rows.length === 0}>
                Select all available
              </Button>
            )}
            <div className="relative w-full md:w-[420px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search crop, variety, location, unit..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border">
        <div className="max-h-[min(52vh,560px)] min-w-[760px] scroll-pb-28 overflow-auto pb-20">
          <div className={`${stockGridClass} sticky top-0 z-10 border-b bg-muted/95 p-3 text-sm font-medium backdrop-blur`}>
            <span>{type === "ToExpo" ? "Crop" : "Stock batch"}</span>
            {type === "ReturnToGreenhouse" ? (
              <>
                <span className="text-right">Received</span>
                <span className="text-right">Sold</span>
                <span className="text-right">Pending</span>
                <span className="text-right">Available</span>
                <span>Selected return</span>
                <span className="text-right">Action</span>
              </>
            ) : (
              <>
                <span>Variety</span>
                <span>Tray Type</span>
                <span>Sowing Date</span>
                <span>Available Quantity</span>
                <span>Quantity</span>
                <span>Selected value</span>
              </>
            )}
          </div>

          {greenhouse.isFetching && type === "ToExpo" && (
            <p className="border-b p-3 text-sm text-muted-foreground">Refreshing matching stock…</p>
          )}

          {rows.map((row) => {
            const quantityValue = Number(quantities[row.id] || 0);
            const isOverQuantity = quantityValue > row.available;
            const configuredPrice = configuredPriceFor(row);
            const expectedAmount = configuredPrice * quantityValue;
            const displayUnit = unitLabel(row, { short: true });
            const stockDescriptor = unitLabel(row);

            return (
              <div
                key={row.id}
                className={`${stockGridClass} border-b p-3 last:border-0`}
              >
                {type === "ToExpo" ? (
                  <>
                    <div>
                      <p className="font-medium">{row.cropTypeName}</p>
                      <p className="text-xs text-muted-foreground">{row.location}</p>
                    </div>
                    <span className="self-center text-sm">{row.variety || "—"}</span>
                    <span className="self-center text-sm">{row.trayType ?? row.stockUnit ?? "—"}</span>
                    <span className="self-center whitespace-nowrap text-sm">
                      <StockSowingDate value={row.sowingDate} source={row.source} />
                    </span>
                    <span className="self-center whitespace-nowrap text-sm font-medium">
                      {displayQuantity(row.available, displayUnit)}
                    </span>
                  </>
                ) : (
                  <div>
                    <p className="font-medium">{row.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.location} · {stockDescriptor}
                    </p>
                  </div>
                )}
                {type === "ReturnToGreenhouse" && (
                  <>
                    <span className="self-center text-right text-sm">{displayQuantity(row.received, displayUnit)}</span>
                    <span className="self-center text-right text-sm">{displayQuantity(row.sold, displayUnit)}</span>
                    <span className="self-center text-right text-sm">{displayQuantity(row.pendingReturn, displayUnit)}</span>
                    <span className="self-center text-right text-sm font-medium">{displayQuantity(row.available, displayUnit)}</span>
                  </>
                )}
                <div className="space-y-1">
                  <Input
                    className={
                      isOverQuantity
                        ? "border-destructive text-destructive focus-visible:ring-destructive"
                        : undefined
                    }
                    aria-invalid={isOverQuantity}
                    type="number"
                    min="0"
                    max={row.available}
                    step="0.0001"
                    value={quantities[row.id] ?? ""}
                    onChange={(event) => patchQuantity(row, event.target.value)}
                  />
                  {isOverQuantity && (
                    <p className="text-xs font-medium text-destructive">
                      Max {displayQuantity(row.available, displayUnit)}
                    </p>
                  )}
                </div>
                <div className="self-center text-right">
                  {type === "ReturnToGreenhouse" ? (
                    <Button type="button" variant="outline" size="sm" onClick={() => setReturnAll(row)}>
                      Return all
                    </Button>
                  ) : quantityValue > 0 && configuredPrice > 0 ? (
                    <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-left">
                      <p>KES {expectedAmount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">
                        {quantityValue.toLocaleString()} × KES {configuredPrice.toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{quantityValue > 0 ? "Price not configured" : "—"}</span>
                  )}
                </div>
              </div>
            );
          })}

          {rows.length === 0 && (
            <div className="p-8">
              <Alert>
                <AlertTitle>{type === "ReturnToGreenhouse" ? "No returnable Expo stock" : "No matching stock found"}</AlertTitle>
                <AlertDescription>
                  {type === "ReturnToGreenhouse"
                    ? "Only received Expo stock with a positive available balance appears here. Stock may already be sold, returned, or reserved by a pending return request."
                    : "Try a different search term or confirm that greenhouse stock is ready for walk-in sale."}
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
        </div>

        <div className="sticky bottom-0 z-10 flex flex-col gap-3 border-t bg-card/95 p-4 text-sm text-muted-foreground shadow-[0_-8px_18px_rgba(15,23,42,0.06)] backdrop-blur md:flex-row md:items-center md:justify-between">
          <span>
            {totalCount} matching batch{totalCount === 1 ? "" : "es"} · {selectedCount} selected
          </span>
          {type === "ToExpo" && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((page) => Math.max(1, page - 1))}
              >
                Previous
              </Button>
              <span>
                Page {pageNumber} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((page) => page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 z-20 -mx-2 border-t bg-background/95 px-2 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-h-5 text-sm">
            {error && <p className="font-medium text-destructive">{error}</p>}
            {hasOverQuantity && (
              <p className="font-medium text-destructive">
                Fix over-quantity line{overQuantityRows.length === 1 ? "" : "s"} before submitting.
              </p>
            )}
            {!error && !hasOverQuantity && (
              <p className="text-muted-foreground">
                {selectedCount} stock batch{selectedCount === 1 ? "" : "es"} selected.
                {type === "ReturnToGreenhouse" ? ` ${displayQty(selectedQuantity)} selected for return.` : ""}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(`/expos/${id}`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit}>
              Submit transfer request
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
